import { useState, useEffect, useMemo } from "react";
import Txn from "./Txn";
import { transactionAPI } from "services";
import useUniversalStore from "store/universalStore";
import {
  OpenCloseBalance,
  ListHeader,
  ShimmerLoader,
  HeaderShimmer
} from "components";
import { formatMoney, debounce } from "utils";

const TxnsListView = ({ animation, setAnimation, hideClosed,handleToggle }) => {
  const txns = useUniversalStore((state) => state.transactions) || [];
  const { accountId, isReminderChanged, write, selectedMonth } = useUniversalStore((state) => state);
  const [loading, setLoading] = useState(false);
  const [openBalance, setOpenBalance] = useState(0);


  // Filter transactions based on hideClosed toggle
  const filteredTnx = useMemo(() => {
    if (hideClosed) {
      return txns.filter((item) => !item.get("cancelled"));
    }
    return txns;
  }, [hideClosed, txns]);


  const monthlyCash = useMemo(() => {
    let cashIn = 0;
    let cashOut = 0;

    txns.forEach(txn => {
      if (!txn.get("cancelled")) {
        const amount = txn.get("amount") || 0;
        if (amount > 0) cashIn += amount;
        else cashOut += Math.abs(amount);
      }
    });

    return {
      in: formatMoney(cashIn),
      out: formatMoney(cashOut),
      totalInOutChange: cashIn - cashOut
    };
  }, [txns]);


  const closeBalance = useMemo(() => {
    return openBalance + monthlyCash.totalInOutChange;
  }, [openBalance, monthlyCash.totalInOutChange]);

  const debouncedLoadAllData = useMemo(() => {
    const fetchFunc = async (mDate, accId, isRemChanged) => {
      if (!accId || !mDate) return;
      try {
        const startDate = new Date(mDate.getFullYear(), mDate.getMonth(), 1, 0, 0, 0, 0);
        const prevMonthEnd = new Date(startDate.getTime() - 1);

        const [txnsResult, balanceResult] = await Promise.all([
          transactionAPI.getTransactionsByAccountIdWithMonth(accId, mDate),
          transactionAPI.sumTillDateByAccount(prevMonthEnd, accId)
        ]);

        write({ transactions: txnsResult });
        setOpenBalance(balanceResult);
      } catch (error) {
        console.error("Error loading Khata data:", error);
      } finally {
        setLoading(false);
      }
    };

    return debounce(fetchFunc, 800);
  }, []);

  useEffect(() => {
    setLoading(true);
    debouncedLoadAllData(selectedMonth, accountId, isReminderChanged);
  }, [selectedMonth, accountId, isReminderChanged]);

  return (
    <div
      className={`row ${animation}`}
      // className={`background mb-auto position-relative z-0 d-flex flex-column animate__slideInRight`}
      onAnimationEnd={() => setAnimation("")}>
      {loading ?
        <div className="row ">
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <div className="shimmer-container">
              <HeaderShimmer />
              {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
            </div>
          </div>
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <div className="shimmer-container">
              <HeaderShimmer />
              {[...Array(6)].map((_, i) => <ShimmerLoader key={i} />)}
            </div>
          </div>
        </div>
        : <div className="row p-0 m-0">
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <ListHeader type="in" amount={monthlyCash.in} title="Received" />
            <OpenCloseBalance type="open" sum={openBalance} />
            {filteredTnx
              ?.filter((txn) => txn.get("amount") > 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  handleToggle={handleToggle}
                  setLoading={setLoading}
                />
              ))}
          </div>
          <div className="col-6 list-group list-group-flush scrollbar-design overflow-auto px-md-3"
            style={{ height: 'calc(100vh - 225px)', maxHeight: 'calc(100vh - 225px)' }}>
            <ListHeader type="out" amount={monthlyCash.out} title="Given" />
            {filteredTnx
              ?.filter((txn) => txn.get("amount") < 0)
              ?.map((txn, i) => (
                <Txn
                  data={txn}
                  key={txn.id || i}
                  handleToggle={handleToggle}
                  setLoading={setLoading}
                />
              ))}
            <OpenCloseBalance type="close" sum={closeBalance} />
          </div>
        </div>}
    </div>
  );
};

export default TxnsListView;